import React from "react";
import { connect } from "react-redux";
import moment from "moment";
import { Button } from "../../Button";
import { Input, Select } from "../../Input";
import { Modal, DeleteModal } from "../../Modal";
import toastMessage from "../../../utils/toastMessage";
import centerQuery from "../../../utils/queries/bloodCenterQuery";
import fetchCorneaRetrievalPlan, {
  createCorneaRetrievalPlan,
  updateCorneaRetrievalPlan,
  deleteCorneaRetrievalPlan,
  recordCorneaPerformance,
} from "../../../utils/queries/corneaRetrievalPlanQuery";
import { defineRoleCenterSite, hasAllCenters } from "../../../utils/handleAccessRoles";
import { getStorage } from "../../../utils/storage";

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => {
  const y = currentYear - 2 + i;
  return { label: String(y), value: y };
});

const QUARTERS = [
  { label: "Q1 (Jan – Mar)", value: 1 },
  { label: "Q2 (Apr – Jun)", value: 2 },
  { label: "Q3 (Jul – Sep)", value: 3 },
  { label: "Q4 (Oct – Dec)", value: 4 },
];

const getISOWeekStart = (year, week) => {
  const jan4 = new Date(year, 0, 4);
  const jan4Day = jan4.getDay() || 7;
  const startOfWeek1 = new Date(jan4);
  startOfWeek1.setDate(jan4.getDate() - (jan4Day - 1));
  const result = new Date(startOfWeek1);
  result.setDate(startOfWeek1.getDate() + (week - 1) * 7);
  return result;
};

class CorneaRetrievalPlan extends React.Component {
  state = {
    isAdmin: false,
    roleCenter: null,

    filterYear: currentYear,
    filterQuarter: null,
    filterCenter: null,
    centers: [],
    plans: [],
    isLoading: false,

    showModal: false,
    showDeleteModal: false,
    showPerformModal: false,
    editRecord: null,
    deleteId: null,
    performRecord: null,

    formCenter: null,
    formHospital: "",
    formWeekNumber: "",
    formYear: currentYear,
    formStartDate: "",
    formEndDate: "",
    formPlannedCorneas: "",
    formError: {},
    isSaving: false,
    isDeleting: false,

    perfCorneas: "",
    perfRemarks: "",
    isSavingPerf: false,
  };

  async componentDidMount() {
    await this.loadRole();
    this.getCenters();
    this.fetchPlans();
  }

  loadRole = async () => {
    const { centerRole } = await defineRoleCenterSite();
    const user = await getStorage();
    const isSuperAdmin = user?.role === "superAdmin";
    const isCentral = hasAllCenters(user?.center);
    const isAdmin = isSuperAdmin || isCentral || !centerRole;
    this.setState({ isAdmin, roleCenter: centerRole });
  };

  getCenters = async () => {
    const data = await centerQuery(this.props.defaultLanguage, { type: "dropdown" });
    this.setState({ centers: data || [] });
  };

  fetchPlans = async () => {
    const { filterYear, filterQuarter, filterCenter, isAdmin, roleCenter } = this.state;
    this.setState({ isLoading: true });
    try {
      const params = { year: filterYear, limit: 200, page: 1 };
      if (filterQuarter?.value) params.quarter = filterQuarter.value;

      if (isAdmin) {
        if (filterCenter?.value) params.center = filterCenter.value;
      } else {
        if (roleCenter) params.center = roleCenter?._id || roleCenter;
      }

      const res = await fetchCorneaRetrievalPlan(this.props.defaultLanguage, params);
      this.setState({ plans: res?.data || [], isLoading: false });
    } catch {
      this.setState({ isLoading: false });
    }
  };

  onWeekChange = (week, year) => {
    const wk = parseInt(week);
    const yr = parseInt(year) || currentYear;
    if (!wk) return;
    const start = getISOWeekStart(yr, wk);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    this.setState({
      formWeekNumber: week,
      formStartDate: moment(start).format("YYYY-MM-DD"),
      formEndDate: moment(end).format("YYYY-MM-DD"),
    });
  };

  openAddModal = () => {
    this.setState({
      showModal: true,
      editRecord: null,
      formCenter: null,
      formHospital: "",
      formWeekNumber: "",
      formYear: currentYear,
      formStartDate: "",
      formEndDate: "",
      formPlannedCorneas: "",
      formError: {},
    });
  };

  openEditModal = (record) => {
    const formCenter = record.center
      ? { label: record.center.name || record.center._id, value: record.center._id || record.center }
      : null;
    this.setState({
      showModal: true,
      editRecord: record,
      formCenter,
      formHospital: record.hospital || "",
      formWeekNumber: String(record.weekNumber || ""),
      formYear: record.year || currentYear,
      formStartDate: record.startDate ? moment(record.startDate).format("YYYY-MM-DD") : "",
      formEndDate: record.endDate ? moment(record.endDate).format("YYYY-MM-DD") : "",
      formPlannedCorneas: String(record.plannedCorneas ?? ""),
      formError: {},
    });
  };

  openPerformModal = (record) => {
    this.setState({
      showPerformModal: true,
      performRecord: record,
      perfCorneas: record.performedCorneas != null ? String(record.performedCorneas) : "",
      perfRemarks: record.remarks || "",
    });
  };

  closeModal = () => this.setState({ showModal: false, editRecord: null });
  closePerformModal = () => this.setState({ showPerformModal: false, performRecord: null });

  validateForm = () => {
    const { formCenter, formHospital, formWeekNumber, formStartDate, formEndDate, formPlannedCorneas } = this.state;
    const errors = {};
    if (!formCenter) errors.formCenter = "Required";
    if (!formHospital.trim()) errors.formHospital = "Required";
    if (!formWeekNumber) errors.formWeekNumber = "Required";
    if (!formStartDate) errors.formStartDate = "Required";
    if (!formEndDate) errors.formEndDate = "Required";
    if (!formPlannedCorneas) errors.formPlannedCorneas = "Required";
    this.setState({ formError: errors });
    return Object.keys(errors).length === 0;
  };

  saveRecord = async () => {
    if (!this.validateForm()) return;
    const { formCenter, formHospital, formWeekNumber, formYear, formStartDate, formEndDate, formPlannedCorneas, editRecord } = this.state;

    this.setState({ isSaving: true });
    try {
      const body = {
        center: formCenter.value,
        hospital: formHospital.trim(),
        weekNumber: Number(formWeekNumber),
        year: Number(formYear),
        startDate: formStartDate,
        endDate: formEndDate,
        plannedCorneas: Number(formPlannedCorneas),
      };

      if (editRecord) {
        body.id = editRecord._id;
        await updateCorneaRetrievalPlan(this.props.defaultLanguage, body);
        toastMessage("success", "Plan updated");
      } else {
        await createCorneaRetrievalPlan(this.props.defaultLanguage, body);
        toastMessage("success", "Plan created");
      }

      this.closeModal();
      this.fetchPlans();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSaving: false });
  };

  savePerformance = async () => {
    const { performRecord, perfCorneas, perfRemarks } = this.state;
    if (!perfCorneas) return toastMessage("error", "Performed corneas count is required");

    this.setState({ isSavingPerf: true });
    try {
      await recordCorneaPerformance(this.props.defaultLanguage, {
        id: performRecord._id,
        performedCorneas: Number(perfCorneas),
        remarks: perfRemarks,
      });
      toastMessage("success", "Performance recorded");
      this.closePerformModal();
      this.fetchPlans();
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || "Save failed");
    }
    this.setState({ isSavingPerf: false });
  };

  confirmDelete = (id) => this.setState({ showDeleteModal: true, deleteId: id });
  closeDeleteModal = () => this.setState({ showDeleteModal: false, deleteId: null });

  deleteRecord = async () => {
    this.setState({ isDeleting: true });
    try {
      await deleteCorneaRetrievalPlan(this.props.defaultLanguage, this.state.deleteId);
      toastMessage("success", "Plan deleted");
      this.closeDeleteModal();
      this.fetchPlans();
    } catch {
      toastMessage("error", "Delete failed");
    }
    this.setState({ isDeleting: false });
  };

  renderSummaryTable() {
    const { plans, filterYear } = this.state;
    if (!plans.length) return null;

    const byHospital = {};
    plans.forEach((p) => {
      if (!byHospital[p.hospital]) byHospital[p.hospital] = { planned: 0, performed: 0, weeks: 0 };
      byHospital[p.hospital].planned += p.plannedCorneas || 0;
      byHospital[p.hospital].performed += p.performedCorneas || 0;
      byHospital[p.hospital].weeks += 1;
    });

    let grandPlanned = 0, grandPerformed = 0;

    return (
      <div id="printable-cornea-plan">
        <style>{`@media print { body > *:not(#printable-cornea-plan) { display: none; } #printable-cornea-plan { display: block !important; } .no-print { display: none !important; } }`}</style>
        <div style={{ textAlign: "center", fontWeight: "bold", fontSize: 15, marginBottom: 4 }}>CORNEA RETRIEVAL PLAN — {filterYear}</div>
        <div style={{ textAlign: "center", fontSize: 12, color: "#555", marginBottom: 12 }}>Rwanda Biomedical Centre / Blood Transfusion Division</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#5b4a99", color: "#fff" }}>
                <th style={thStyle}>Hospital</th>
                <th style={thStyle}>Weeks Planned</th>
                <th style={thStyle}>Planned Corneas</th>
                <th style={thStyle}>Performed Corneas</th>
                <th style={thStyle}>Achievement %</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(byHospital).map(([hospital, d]) => {
                grandPlanned += d.planned;
                grandPerformed += d.performed;
                const pct = d.planned > 0 ? Math.round((d.performed / d.planned) * 100) : 0;
                return (
                  <tr key={hospital}>
                    <td style={tdStyle}>{hospital}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{d.weeks}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{d.planned}</td>
                    <td style={{ ...tdStyle, textAlign: "center", color: d.performed >= d.planned && d.planned > 0 ? "#1a7c3e" : d.performed > 0 ? "#c0392b" : "#888" }}>
                      {d.performed || "—"}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: "bold", color: pct >= 100 ? "#1a7c3e" : pct >= 80 ? "#e67e22" : "#c0392b" }}>
                      {d.planned > 0 ? `${pct}%` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: "#f0f0f0", fontWeight: "bold" }}>
                <td style={tdStyle}>TOTAL</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>{plans.length}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>{grandPlanned}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>{grandPerformed}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  {grandPlanned > 0 ? `${Math.round((grandPerformed / grandPlanned) * 100)}%` : "—"}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  }

  renderPlanList() {
    const { plans, isLoading } = this.state;
    if (isLoading) return <div style={{ padding: 24, textAlign: "center" }}>Loading...</div>;
    if (!plans.length) return <div style={{ padding: 24, textAlign: "center", color: "#888" }}>No records found. Click "+ Add" to create one.</div>;

    return (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#f0f0f0" }}>
              <th style={thStyle}>Center</th>
              <th style={thStyle}>Hospital</th>
              <th style={thStyle}>Week</th>
              <th style={thStyle}>Start Date</th>
              <th style={thStyle}>End Date</th>
              <th style={thStyle}>Planned</th>
              <th style={thStyle}>Performed</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((p) => (
              <tr key={p._id}>
                <td style={tdStyle}>{p.center?.name || "—"}</td>
                <td style={tdStyle}>{p.hospital}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>W{p.weekNumber}</td>
                <td style={tdStyle}>{moment(p.startDate).format("DD/MM")}</td>
                <td style={tdStyle}>{moment(p.endDate).format("DD/MM")}</td>
                <td style={{ ...tdStyle, textAlign: "center" }}>{p.plannedCorneas}</td>
                <td style={{ ...tdStyle, textAlign: "center", color: p.performedCorneas != null ? (p.performedCorneas >= p.plannedCorneas ? "#1a7c3e" : "#c0392b") : "#aaa" }}>
                  {p.performedCorneas ?? "—"}
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <span style={statusBadge(p.status)}>{p.status}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: "center" }}>
                  <button onClick={() => this.openPerformModal(p)} style={actionBtnStyle("#1a7c3e")}>✓ Record</button>
                  {" "}
                  <button onClick={() => this.openEditModal(p)} style={actionBtnStyle("#2980b9")}>Edit</button>
                  {" "}
                  <button onClick={() => this.confirmDelete(p._id)} style={actionBtnStyle("#c0392b")}>Del</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const {
      filterYear, filterQuarter, filterCenter, centers,
      showModal, showDeleteModal, showPerformModal, editRecord,
      formCenter, formHospital, formWeekNumber, formYear, formStartDate, formEndDate,
      formPlannedCorneas, formError, isSaving, isDeleting,
      perfCorneas, perfRemarks, isSavingPerf, performRecord, plans,
    } = this.state;

    return (
      <div>
        <div className="no-print row" style={{ marginBottom: 16, alignItems: "flex-end" }}>
          <div className="col-md-2">
            <Select label="Year" options={YEARS} value={YEARS.find((y) => y.value === filterYear) || null}
              onChange={(v) => this.setState({ filterYear: v?.value || currentYear })} />
          </div>
          <div className="col-md-2">
            <Select label="Quarter" options={QUARTERS} value={filterQuarter}
              onChange={(v) => this.setState({ filterQuarter: v })} isClearable />
          </div>
          <div className="col-md-3">
            <Select label="Center" options={centers} value={filterCenter}
              onChange={(v) => this.setState({ filterCenter: v })} isClearable />
          </div>
          <div className="col-md-3" style={{ display: "flex", gap: 6, alignItems: "flex-end" }}>
            <Button text="Search" onPress={this.fetchPlans} />
            <Button text="+ Add" onPress={this.openAddModal} className="btn-default" />
            {plans.length > 0 && <Button text="Print" onPress={() => window.print()} className="btn-default" />}
          </div>
        </div>

        {this.renderSummaryTable()}

        <div style={{ marginTop: 24 }} className="no-print">
          <div style={{ fontWeight: "bold", fontSize: 14, marginBottom: 8, borderBottom: "1px solid #ddd", paddingBottom: 4 }}>Weekly Records</div>
          {this.renderPlanList()}
        </div>

        <Modal show={showModal} handleClose={this.closeModal} title={editRecord ? "Edit Cornea Plan" : "Add Cornea Plan"} size="md">
          <div className="modal-body">
            <div className="row">
              <div className="col-md-12">
                <Select label="Center (RCBT)" options={centers} value={formCenter}
                  onChange={(v) => this.setState({ formCenter: v })} error={formError.formCenter} />
              </div>
              <div className="col-md-12">
                <Input label="Hospital Name" value={formHospital}
                  onChange={(e) => this.setState({ formHospital: e.target.value })} error={formError.formHospital} />
              </div>
              <div className="col-md-6">
                <Select label="Year" options={YEARS} value={YEARS.find((y) => y.value === formYear) || null}
                  onChange={(v) => { this.setState({ formYear: v?.value || currentYear }); this.onWeekChange(formWeekNumber, v?.value); }} />
              </div>
              <div className="col-md-6">
                <Input label="Week Number" type="number" min={1} max={53} value={formWeekNumber}
                  onChange={(e) => this.onWeekChange(e.target.value, formYear)} error={formError.formWeekNumber} />
              </div>
              <div className="col-md-6">
                <Input label="Start Date (Monday)" type="date" value={formStartDate}
                  onChange={(e) => this.setState({ formStartDate: e.target.value })} error={formError.formStartDate} />
              </div>
              <div className="col-md-6">
                <Input label="End Date (Sunday)" type="date" value={formEndDate}
                  onChange={(e) => this.setState({ formEndDate: e.target.value })} error={formError.formEndDate} />
              </div>
              <div className="col-md-12">
                <Input label="Planned Corneas" type="number" value={formPlannedCorneas}
                  onChange={(e) => this.setState({ formPlannedCorneas: e.target.value })} error={formError.formPlannedCorneas} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" onPress={this.closeModal} className="btn-default" />
            <Button text={editRecord ? "Update" : "Save"} onPress={this.saveRecord} isSubmitting={isSaving} />
          </div>
        </Modal>

        <Modal show={showPerformModal} handleClose={this.closePerformModal}
          title={`Record Performance — ${performRecord ? `${performRecord.hospital} W${performRecord.weekNumber}` : ""}`} size="md">
          <div className="modal-body">
            {performRecord && (
              <div style={{ background: "#f8f9fa", borderRadius: 6, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
                <strong>Hospital:</strong> {performRecord.hospital} &nbsp;&nbsp;
                <strong>Week:</strong> {performRecord.weekNumber} &nbsp;&nbsp;
                <strong>Planned:</strong> {performRecord.plannedCorneas} corneas
              </div>
            )}
            <div className="row">
              <div className="col-md-12">
                <Input label="Performed Corneas" type="number" value={perfCorneas}
                  onChange={(e) => this.setState({ perfCorneas: e.target.value })} />
              </div>
              <div className="col-md-12">
                <Input label="Remarks" value={perfRemarks}
                  onChange={(e) => this.setState({ perfRemarks: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button text="Cancel" onPress={this.closePerformModal} className="btn-default" />
            <Button text="Save Performance" onPress={this.savePerformance} isSubmitting={isSavingPerf} />
          </div>
        </Modal>

        <Modal show={showDeleteModal} handleClose={this.closeDeleteModal} title="Delete Plan">
          <DeleteModal handleCloseModal={this.closeDeleteModal} onDelete={this.deleteRecord} isDeleting={isDeleting} />
        </Modal>
      </div>
    );
  }
}

const thStyle = { border: "1px solid #ccc", padding: "6px 8px", textAlign: "left", fontWeight: "bold", fontSize: 12, whiteSpace: "nowrap" };
const tdStyle = { border: "1px solid #ddd", padding: "5px 8px", whiteSpace: "nowrap" };
const actionBtnStyle = (color) => ({ background: color, color: "#fff", border: "none", borderRadius: 3, padding: "3px 8px", cursor: "pointer", fontSize: 11 });
const statusBadge = (status) => ({
  display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: "bold",
  background: status === "performed" ? "#d4edda" : status === "cancelled" ? "#f8d7da" : "#fff3cd",
  color: status === "performed" ? "#155724" : status === "cancelled" ? "#721c24" : "#856404",
});

const mapStateToProps = (state) => {
  const { defaultLanguage } = state.Language;
  return { defaultLanguage };
};

export default connect(mapStateToProps)(CorneaRetrievalPlan);
